'use client'

import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  IconArrowRight,
  IconBook2,
  IconCheck,
  IconCompass,
  IconSchool,
  IconSparkles
} from '@tabler/icons-react'
import { useTranslations } from 'next-intl'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/useAuth'
import { useUpsertLearningPreferences } from '@/hooks/useLearningPreferences'
import { Link, useRouter } from '@/i18n/routing'
import { analytics } from '@/lib/analytics'
import { canonicalRoutes } from '@/lib/product-routes'
import {
  completeOnboardingSetup,
  getOnboardingStoryRecommendation,
  skipOnboarding,
  type OnboardingGoal,
  type OnboardingLevel
} from '@/lib/onboarding'
import { cn } from '@/lib/utils'
import type { StoryLearningIndexItem } from '@/content/stories'

const goalOptions: Array<{
  value: OnboardingGoal
  icon: typeof IconSchool
}> = [
  { value: 'hsk', icon: IconSchool },
  { value: 'tocfl', icon: IconCompass },
  { value: 'daily-reading', icon: IconBook2 },
  { value: 'not-sure', icon: IconSparkles }
]

const hskLevelOptions: OnboardingLevel[] = [
  'hsk-1',
  'hsk-2',
  'hsk-3',
  'hsk-4',
  'hsk-5',
  'hsk-6',
  'not-sure'
]

const tocflLevelOptions: OnboardingLevel[] = [
  'tocfl-1',
  'tocfl-2',
  'tocfl-3',
  'tocfl-4',
  'not-sure'
]

const mixedLevelOptions: OnboardingLevel[] = [
  'hsk-1',
  'hsk-3',
  'hsk-5',
  'tocfl-2',
  'not-sure'
]

function getLevelOptions(goal: OnboardingGoal) {
  if (goal === 'hsk') return hskLevelOptions
  if (goal === 'tocfl') return tocflLevelOptions
  return mixedLevelOptions
}

function formatLevelLabel(
  level: OnboardingLevel,
  t: ReturnType<typeof useTranslations>
) {
  if (level === 'not-sure') return t('levelNotSure')
  return level.replace('-', ' ').toUpperCase()
}

export function OnboardingPageClient({
  locale,
  storyLearningIndex
}: {
  locale: string
  storyLearningIndex: StoryLearningIndexItem[]
}) {
  const t = useTranslations('onboarding')
  const router = useRouter()
  const { user, isLoading } = useAuth()
  const upsertLearningPreferences = useUpsertLearningPreferences()
  const [goal, setGoal] = useState<OnboardingGoal>('hsk')
  const [level, setLevel] = useState<OnboardingLevel>('hsk-1')
  const levelOptions = getLevelOptions(goal)

  useEffect(() => {
    analytics.onboardingStarted()
  }, [])

  useEffect(() => {
    if (!isLoading && !user) {
      const loginHref = `/login?redirect=${encodeURIComponent(`/${locale}/onboarding`)}`
      router.replace(loginHref as Parameters<typeof router.replace>[0])
    }
  }, [isLoading, locale, router, user])

  const recommendation = useMemo(
    () =>
      getOnboardingStoryRecommendation({
        stories: storyLearningIndex,
        goal,
        level
      }),
    [goal, level, storyLearningIndex]
  )

  const finishSetup = () => {
    if (!recommendation) return

    completeOnboardingSetup({
      goal,
      level,
      recommendedStorySlug: recommendation.slug
    })
    upsertLearningPreferences.mutate({
      goal,
      level
    })
    analytics.onboardingSetupCompleted(goal, level, recommendation.slug)
    router.push(canonicalRoutes.today)
  }

  const skip = () => {
    skipOnboarding()
    analytics.onboardingSkipped()
    router.push(canonicalRoutes.today)
  }

  if (isLoading || !user) {
    return (
      <OnboardingFrame title={t('title')} description={t('description')}>
        <div className="grid gap-3">
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-36 rounded-xl" />
        </div>
      </OnboardingFrame>
    )
  }

  return (
    <OnboardingFrame title={t('title')} description={t('description')}>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-5">
          <section className="space-y-3">
            <SectionHeading
              eyebrow={t('stepOne')}
              title={t('goalTitle')}
              description={t('goalDescription')}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              {goalOptions.map((option) => {
                const Icon = option.icon
                const selected = goal === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      const nextLevelOptions = getLevelOptions(option.value)
                      setGoal(option.value)
                      if (!nextLevelOptions.includes(level)) {
                        setLevel(nextLevelOptions[0] ?? 'not-sure')
                      }
                    }}
                    className={cn(
                      'bg-card text-card-foreground focus-visible:ring-ring rounded-xl border p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none',
                      selected
                        ? 'border-primary/55 ring-primary/15 ring-4'
                        : 'border-border/80'
                    )}
                  >
                    <span className="flex items-start gap-3">
                      <span
                        className={cn(
                          'flex h-10 w-10 items-center justify-center rounded-lg',
                          selected
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 font-semibold">
                          {t(`goals.${option.value}.title`)}
                          {selected && <IconCheck className="h-4 w-4" />}
                        </span>
                        <span className="text-muted-foreground mt-1 block text-sm leading-6">
                          {t(`goals.${option.value}.description`)}
                        </span>
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          </section>

          <section className="space-y-3">
            <SectionHeading
              eyebrow={t('stepTwo')}
              title={t('levelTitle')}
              description={t('levelDescription')}
            />
            <div className="flex flex-wrap gap-2">
              {levelOptions.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLevel(item)}
                  className={cn(
                    'focus-visible:ring-ring rounded-full border px-4 py-2 text-sm font-semibold transition focus-visible:ring-2 focus-visible:outline-none',
                    level === item
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border/80 bg-card text-muted-foreground hover:text-foreground'
                  )}
                >
                  {formatLevelLabel(item, t)}
                </button>
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:pt-9">
          <Card className="rounded-xl shadow-sm">
            <CardContent className="space-y-4 p-5">
              <Badge variant="secondary">{t('recommendationBadge')}</Badge>
              <div>
                <h2 className="text-lg font-semibold">
                  {recommendation?.title_en ?? t('recommendationFallbackTitle')}
                </h2>
                <p className="text-muted-foreground mt-2 text-sm leading-6">
                  {recommendation
                    ? t('recommendationDescription', {
                        level: recommendation.level
                          .replace('-', ' ')
                          .toUpperCase()
                      })
                    : t('recommendationFallbackDescription')}
                </p>
              </div>
              {recommendation && (
                <div className="bg-muted/45 rounded-lg px-3 py-2 text-sm">
                  <p className="font-semibold">{t('firstWinTitle')}</p>
                  <p className="text-muted-foreground mt-1 leading-6">
                    {t('firstWinDescription')}
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  onClick={finishSetup}
                  disabled={!recommendation}
                  className="w-full"
                >
                  {t('finishButton')}
                  <IconArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={skip}
                  className="w-full"
                >
                  {t('skipButton')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </OnboardingFrame>
  )
}

function OnboardingFrame({
  title,
  description,
  children
}: {
  title: string
  description: string
  children: ReactNode
}) {
  const t = useTranslations('onboarding')

  return (
    <main className="mx-auto flex min-h-[calc(100dvh-var(--product-shell-header-height))] w-full max-w-6xl flex-col px-4 py-6 md:px-6 md:py-10">
      <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <Badge variant="secondary" className="mb-3">
            {title}
          </Badge>
          <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
            {description}
          </h1>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link href={canonicalRoutes.today}>{t('todayLink')}</Link>
        </Button>
      </div>
      {children}
    </main>
  )
}

function SectionHeading({
  eyebrow,
  title,
  description
}: {
  eyebrow: string
  title: string
  description: string
}) {
  return (
    <div>
      <p className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">
        {eyebrow}
      </p>
      <h2 className="mt-1 text-xl font-semibold tracking-tight">{title}</h2>
      <p className="text-muted-foreground mt-1 max-w-2xl text-sm leading-6">
        {description}
      </p>
    </div>
  )
}
